import { Box, ButtonBase, Paper, Stack, Typography } from '@mui/material';

import { DonutChart, type DonutSegment } from './DonutChart';
import { percentagesSummingTo100 } from './insightMath';
import { categoricalHues, sequentialBlues } from './palette';
import type { SingleSelectInsight } from './types';

export type DonutOptionSelection = {
  insightId: string;
  insightTitle: string;
  answerLabel: string;
  /** Raw value behind answerLabel (e.g. a custom slot's enum value) — set when the insight carries one. */
  answerValue?: string;
};

export type DonutInsightCardProps = {
  insight: SingleSelectInsight;
  /** Featured cards show the raw response count alongside the percentage. */
  showCounts?: boolean;
  /** Drill-down into the door-visit list for the clicked answer. Omit to keep the card non-interactive. */
  onOptionSelect?: (selection: DonutOptionSelection) => void;
};

const legendValue = (count: number, percent: number, showCounts: boolean): string =>
  showCounts ? `${count} · ${percent}%` : `${percent}%`;

// Renders a single-select insight as a donut + per-option legend. The option
// order and colors come straight from the payload, so a new/changed option just
// appears — no per-question code (SFOA-35 AC #2, #4).
export const DonutInsightCard = ({ insight, showCounts = false, onOptionSelect }: DonutInsightCardProps) => {
  const { id, title, scale, options, responseCount, visitCount } = insight;
  const colors = scale === 'categorical' ? categoricalHues(options.length) : sequentialBlues(options.length);
  // `responseCount` is the single source of truth for both the percentage
  // denominator and the displayed count, so the legend and the "N responses of
  // M visits" caption can never disagree on the total.
  const percentages = percentagesSummingTo100(
    options.map(option => option.count),
    responseCount,
  );
  const segments: DonutSegment[] = options.map((option, index) => ({
    label: option.label,
    count: option.count,
    color: colors[index],
    value: option.value,
  }));

  const selectOption = (answerLabel: string, answerValue?: string): void => {
    onOptionSelect?.({ insightId: id, insightTitle: title, answerLabel, answerValue });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, height: '100%', minWidth: 0 }}>
      <Stack sx={{ mb: 2, gap: 0.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: 'break-word' }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {responseCount} responses of {visitCount} visits.
        </Typography>
      </Stack>
      <Stack direction="row" sx={{ gap: 3, alignItems: 'center', minWidth: 0 }}>
        <DonutChart
          segments={segments}
          centerValue={responseCount}
          centerLabel="responses"
          ariaLabel={`${title}: ${responseCount} responses`}
          onSegmentClick={onOptionSelect ? segment => selectOption(segment.label, segment.value) : undefined}
        />
        <Stack component="ul" sx={{ flexGrow: 1, minWidth: 0, listStyle: 'none', p: 0, m: 0, gap: 1 }}>
          {options.map((option, index) => {
            const rowContent = (
              <>
                <Box
                  sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: colors[index], flexShrink: 0 }}
                  aria-hidden
                />
                <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {legendValue(option.count, percentages[index], showCounts)}
                </Typography>
              </>
            );
            return (
              <Stack key={option.label} component="li" direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                {onOptionSelect ? (
                  <ButtonBase
                    onClick={() => selectOption(option.label, option.value)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left', borderRadius: 1 }}
                  >
                    {rowContent}
                  </ButtonBase>
                ) : (
                  rowContent
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};
